// src/gestion-logistics/components/PackageManager.tsx
import React, { useState } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Space,
  Popconfirm,
  message,
  Modal,
  Grid,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Package, Container } from "../types/shipment.types";

const { Option } = Select;
const { TextArea } = Input;

interface PackageManagerProps {
  packages: Package[];
  containers?: Container[];
  onChange: (packages: Package[]) => void;
  disabled?: boolean;
  showContainerLink?: boolean;
}

/**
 * Composant de gestion des colis (pour groupage ou détail)
 * Permet de lier des colis à des conteneurs
 */
const PackageManager: React.FC<PackageManagerProps> = ({
  packages = [],
  containers = [],
  onChange,
  disabled = false,
  showContainerLink = true,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPackage, setEditingPackage] = useState<{
    package: Package;
    index: number | null;
  } | null>(null);
  const [form] = Form.useForm();

  // Générer automatiquement un numéro de colis
  const generatePackageNumber = () => {
    const nextNumber = packages.length + 1;
    const packageNumber = `PKG${String(nextNumber).padStart(5, '0')}`;
    form.setFieldsValue({ package_number: packageNumber });
    message.success(`Numéro généré: ${packageNumber}`);
  };

  // Ouvrir modal pour ajouter/éditer
  const handleOpenModal = (pkg?: Package, index?: number) => {
    if (pkg) {
      form.setFieldsValue(pkg);
      setEditingPackage({ package: pkg, index: index ?? null });
    } else {
      form.resetFields();
      form.setFieldsValue({
        is_dangerous: false,
        is_fragile: false,
        requires_temperature_control: false,
      });
      setEditingPackage(null);
    }
    setIsModalVisible(true);
  };

  // Sauvegarder le colis
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingPackage && editingPackage.index !== null) {
        // Édition
        const newPackages = [...packages];
        newPackages[editingPackage.index] = {
          ...newPackages[editingPackage.index],
          ...values,
        };
        onChange(newPackages);
        message.success("Colis modifié");
      } else {
        // Ajout
        onChange([...packages, values]);
        message.success("Colis ajouté");
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingPackage(null);
    } catch (error) {
      console.error("[PACKAGE_MANAGER] Validation failed:", error);
    }
  };

  // Supprimer un colis
  const handleDelete = (index: number) => {
    const newPackages = packages.filter((_, i) => i !== index);
    onChange(newPackages);
    message.success("Colis supprimé");
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "N° Colis",
      dataIndex: "package_number",
      key: "package_number",
      width: 150,
      render: (text: string) => text || "-",
    },
    ...(showContainerLink && containers.length > 0
      ? [
          {
            title: "Conteneur",
            dataIndex: "container_id",
            key: "container_id",
            width: 150,
            render: (containerId: number) => {
              const container = containers.find((c) => c.id === containerId);
              return container?.container_number || "-";
            },
          },
        ]
      : []),
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text: string) => text || "-",
    },
    {
      title: "Poids (kg)",
      dataIndex: "weight",
      key: "weight",
      width: 100,
      render: (weight: number) => (weight !== undefined ? weight : "-"),
    },
    {
      title: "Volume (m³)",
      dataIndex: "volume",
      key: "volume",
      width: 100,
      render: (volume: number) => (volume !== undefined ? volume : "-"),
    },
    {
      title: "Propriétaire",
      dataIndex: "owner_client_name",
      key: "owner_client_name",
      width: 150,
      render: (text: string) => text || "-",
    },
    {
      title: "Dangereux",
      dataIndex: "is_dangerous",
      key: "is_dangerous",
      width: 100,
      render: (isDangerous: boolean, record: Package) => (
        <span style={{ color: isDangerous ? "red" : "inherit" }}>
          {isDangerous ? `Oui (${record.un_number || "N/A"})` : "Non"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right" as const,
      render: (_: any, record: Package, index: number) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleOpenModal(record, index)}
            disabled={disabled}
            size="small"
          >
            Voir
          </Button>
          <Popconfirm
            title="Supprimer ce colis ?"
            onConfirm={() => handleDelete(index)}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={disabled}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="dashed"
          onClick={() => handleOpenModal()}
          icon={<PlusOutlined />}
          disabled={disabled}
          block
        >
          Ajouter un colis
        </Button>
      </div>

      <Table
        dataSource={packages}
        columns={columns}
        rowKey={(record, index) => `package-${index}`}
        pagination={false}
        scroll={{ x: showContainerLink && containers.length > 0 ? 1100 : 900 }}
        size="small"
        bordered
        locale={{
          emptyText: "Aucun colis. Cliquez sur 'Ajouter' pour commencer.",
        }}
      />

      {packages.length > 0 && (
        <div style={{ marginTop: 8, color: "#999", fontSize: 12 }}>
          {packages.length} colis - Poids total:{" "}
          {Number(packages.reduce((sum, p) => sum + (p.weight || 0), 0)).toFixed(2)} kg
        </div>
      )}

      {/* Modal de détails du colis */}
      <Modal
        title={
          editingPackage ? "Modifier le colis" : "Ajouter un nouveau colis"
        }
        open={isModalVisible}
        onOk={handleSave}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingPackage(null);
        }}
        className={isMobile ? "mobile-fullscreen-modal" : undefined}
        centered={!isMobile}
        width={isMobile ? "100vw" : 700}
        style={isMobile ? { top: 0, paddingBottom: 0, margin: 0, maxWidth: "100vw" } : undefined}
        styles={
          isMobile
            ? {
                content: { borderRadius: 0 },
                body: {
                  maxHeight: "calc(100vh - 120px)",
                  overflowY: "auto",
                  padding: 12,
                },
              }
            : {
                body: {
                  maxHeight: "calc(100vh - 200px)",
                  overflowY: "auto",
                },
              }
        }
        okText="Sauvegarder"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          {/* Identification */}
          <div style={{ marginBottom: 16, fontWeight: 600 }}>
            Identification
          </div>

          <Form.Item
            label="Numéro de colis"
            name="package_number"
            rules={[
              { required: true, message: "Le numéro de colis est obligatoire" },
              { max: 50, message: "Maximum 50 caractères" }
            ]}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Input placeholder="Ex: PKG-001" />
              <Button
                icon={<ThunderboltOutlined />}
                onClick={generatePackageNumber}
                title="Générer automatiquement"
              >
                Auto
              </Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item label="Numéro de tracking" name="tracking_number">
            <Input placeholder="Numéro de suivi" />
          </Form.Item>

          {showContainerLink && containers.length > 0 && (
            <Form.Item
              label="Conteneur associé"
              name="container_id"
              rules={[
                { required: true, message: "Le conteneur est obligatoire - chaque colis doit être rattaché à un conteneur" }
              ]}
            >
              <Select placeholder="Sélectionner un conteneur">
                {containers.map((container, idx) => (
                  <Option key={container.id || idx} value={container.id}>
                    {container.container_number || `Conteneur ${idx + 1}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Caractéristiques physiques */}
          <div style={{ marginBottom: 16, marginTop: 24, fontWeight: 600 }}>
            Caractéristiques physiques
          </div>

          <Form.Item label="Description" name="description">
            <TextArea rows={2} placeholder="Description de la marchandise" />
          </Form.Item>

          <Space style={{ width: "100%" }} size="large">
            <Form.Item label="Poids (kg)" name="weight">
              <InputNumber min={0} step={0.01} placeholder="0.00" />
            </Form.Item>

            <Form.Item label="Volume (m³)" name="volume">
              <InputNumber min={0} step={0.001} placeholder="0.000" />
            </Form.Item>

            <Form.Item label="Dimensions" name="dimensions">
              <Input placeholder="L x l x h (cm)" />
            </Form.Item>
          </Space>

          {/* Propriétaire (pour groupage) */}
          <div style={{ marginBottom: 16, marginTop: 24, fontWeight: 600 }}>
            Propriétaire (pour groupage multi-clients)
          </div>

          <Form.Item label="Nom du propriétaire" name="owner_client_name">
            <Input placeholder="Nom du client propriétaire" />
          </Form.Item>

          {/* Classification douanière */}
          <div style={{ marginBottom: 16, marginTop: 24, fontWeight: 600 }}>
            Classification et douane
          </div>

          <Space style={{ width: "100%" }} size="large">
            <Form.Item label="Type de colis" name="package_type">
              <Select placeholder="Type" style={{ width: 150 }}>
                <Option value="carton">Carton</Option>
                <Option value="palette">Palette</Option>
                <Option value="caisse">Caisse bois</Option>
                <Option value="sac">Sac</Option>
                <Option value="autre">Autre</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Code HS" name="hs_code">
              <Input placeholder="Code douanier" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item label="Valeur" name="value">
              <InputNumber min={0} placeholder="0.00" style={{ width: 150 }} />
            </Form.Item>
          </Space>

          {/* Conditions spéciales */}
          <div style={{ marginBottom: 16, marginTop: 24, fontWeight: 600 }}>
            Conditions spéciales
          </div>

          <Space direction="vertical">
            <Form.Item
              name="is_dangerous"
              valuePropName="checked"
              style={{ marginBottom: 8 }}
            >
              <Checkbox>Marchandise dangereuse</Checkbox>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.is_dangerous !== curr.is_dangerous}
            >
              {({ getFieldValue }) =>
                getFieldValue("is_dangerous") ? (
                  <Form.Item
                    label="Numéro ONU"
                    name="un_number"
                    style={{ marginLeft: 24 }}
                  >
                    <Input placeholder="Ex: UN1203" style={{ width: 200 }} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="is_fragile"
              valuePropName="checked"
              style={{ marginBottom: 8 }}
            >
              <Checkbox>Fragile</Checkbox>
            </Form.Item>

            <Form.Item
              name="requires_temperature_control"
              valuePropName="checked"
            >
              <Checkbox>Contrôle de température requis</Checkbox>
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default PackageManager;
